import type { RelatieItem } from '../services/api/relationships.ts';
import type { Relationship } from '../types.ts';

/** De rol van de nieuwe (of gekoppelde) persoon ten opzichte van de referentie. */
export type RelatieRol = 'father' | 'mother' | 'parent' | 'child' | 'spouse' | 'sibling';

const NIEUWE_PERSOON_IS = {
  father: 'parent',
  mother: 'parent',
  parent: 'parent',
  child: 'child',
  spouse: 'partner',
  sibling: 'sibling',
} as const;

/**
 * #1825 — één plek die de richting van een relatie kent. Bij POST /people is
 * de nieuwe persoon het onderwerp; bij /relationships/set de `personId`.
 */
export function relatieItemVoor(
  rol: RelatieRol,
  anderePersoonId: string,
  extra: { relationSubtype?: string; events?: object[]; parentIds?: string[] } = {}
): RelatieItem {
  return {
    kind: 'relative',
    otherPersonId: anderePersoonId,
    newPersonIs: NIEUWE_PERSOON_IS[rol],
    ...extra,
  };
}

/** Biologische ouder zoals de server telt (repos/relationships.js findBiologicalParentIds). */
const isBioOuderRij = (r: Relationship) =>
  r.type === 'parent-child' && (!r.relationSubtype || r.relationSubtype === 'biological');

/**
 * #1825 — bestaande persoon koppelen als broer/zus. /set legt een rij naar elke
 * bio-ouder van de referentie en is alles-of-niets: een ouder die al aan de
 * gekozen persoon hangt gaf 409 voor de hele koppeling. Alleen de ontbrekende
 * ouders dus; `null` = ze zijn er allemaal al. Zonder bio-ouders gaat het item
 * kaal mee en meldt de server SIBLING_WITHOUT_PARENTS.
 */
export function broerZusItem(
  refId: string,
  gekozenId: string,
  relaties: Relationship[],
  /** Ouders die de gebruiker uitvinkte (halfbroer/-zus). */
  uitgevinkt: string[] = []
): RelatieItem | null {
  const bio = relaties
    .filter((r) => isBioOuderRij(r) && r.personB === refId && !uitgevinkt.includes(r.personA))
    .map((r) => r.personA);
  if (bio.length === 0) return relatieItemVoor('sibling', refId);
  const ontbrekend = bio.filter((p) => !relaties.some((r) => isOuderRij(r, p, gekozenId)));
  return ontbrekend.length ? relatieItemVoor('sibling', refId, { parentIds: ontbrekend }) : null;
}

/**
 * #1825 — dezelfde regel als de server voor "bestaat al" (23505 op
 * `unique_relationship (person_id, related_person_id, type)`, migratie 099, en
 * `unique_spouse_pair_any_direction`, migratie 165). /set schrijft ouderrijen
 * altijd met DB-type 'parent' (client 'parent-child'), ongeacht het subtype; een
 * adoptie-, pleeg- of voogdrij heeft een ander type en botst dus niet.
 */
function isOuderRij(r: Relationship, ouder: string, kind: string): boolean {
  return (
    (r.type === 'parent-child' || (r.type as string) === 'parent') &&
    r.personA === ouder &&
    r.personB === kind
  );
}

const isPartnerRij = (r: Relationship, a: string, b: string) =>
  r.type === 'spousal' &&
  ((r.personA === a && r.personB === b) || (r.personA === b && r.personB === a));

/**
 * #1825 — de bestaande rijen die precies de relatie van `item` vormen (leeg = bestaat
 * niet, of niet helemaal). `onderwerpId` is de persoon die /relationships/set koppelt
 * (`personId`); `newPersonIs` is diens rol t.o.v. de ander. Een broer/zus-item zonder
 * `parentIds` kan niet "al bestaan" (de server meldt dan SIBLING_WITHOUT_PARENTS).
 */
export function bestaandeRijen(
  item: RelatieItem,
  onderwerpId: string,
  relaties: Relationship[]
): Relationship[] {
  const zoek = (pred: (r: Relationship) => boolean) => relaties.filter(pred).slice(0, 1);
  if (item.kind === 'between')
    return zoek((r) => isPartnerRij(r, item.personIds[0], item.personIds[1]));
  const ander = item.otherPersonId;
  switch (item.newPersonIs) {
    case 'parent':
      return zoek((r) => isOuderRij(r, onderwerpId, ander));
    case 'child':
      return zoek((r) => isOuderRij(r, ander, onderwerpId));
    case 'partner':
      return zoek((r) => isPartnerRij(r, onderwerpId, ander));
    case 'sibling': {
      const rijen = (item.parentIds ?? []).map((p) =>
        relaties.find((r) => isOuderRij(r, p, onderwerpId))
      );
      return rijen.length && rijen.every(Boolean) ? (rijen as Relationship[]) : [];
    }
  }
}

/** Bestaat de relatie die `item` vraagt al (zie `bestaandeRijen`)? */
export const relatieBestaat = (item: RelatieItem, onderwerpId: string, relaties: Relationship[]) =>
  bestaandeRijen(item, onderwerpId, relaties).length > 0;

/**
 * #1825 I5 — nog niet opgeslagen relatie-items (de nieuwe persoon `onderwerpId` is het
 * onderwerp) als relatierijen, zodat dezelfde regels (ouderKeuze, kanBroerZus, al gekoppeld)
 * ze meetellen. Partner-rijen krijgen type `spousal`, ouderrijen `parent-child`.
 */
export function rijenVoorItems(items: RelatieItem[], onderwerpId: string): Relationship[] {
  const rij = (a: string, b: string, type: Relationship['type'], sub?: string, n = 0) =>
    ({
      id: `concept-${onderwerpId}-${a}-${b}-${n}`,
      projectId: '',
      personA: a,
      personB: b,
      type,
      relationSubtype: sub,
    }) as Relationship;
  return items.flatMap((i): Relationship[] => {
    if (i.kind === 'between')
      return [rij(i.personIds[0], i.personIds[1], 'spousal', i.relationSubtype)];
    const ander = i.otherPersonId;
    switch (i.newPersonIs) {
      case 'parent':
        return [rij(onderwerpId, ander, 'parent-child', i.relationSubtype)];
      case 'child':
        return [rij(ander, onderwerpId, 'parent-child', i.relationSubtype)];
      case 'partner':
        return [rij(onderwerpId, ander, 'spousal', i.relationSubtype)];
      case 'sibling':
        return (i.parentIds ?? []).map((p, n) => rij(p, onderwerpId, 'parent-child', undefined, n));
    }
  });
}
